import * as R from 'ramda'
import {ProductCalculationDetailsAction} from './ProductCalculationDetailsActions'
import * as Result from '../../Data/Result'
import {
    ProductCalculationDetailsFeedback,
    ProductCalculationDetailsRequestIdentifier
} from './ProductCalculationDetailsFeedback'
import {
    EditablePriceDefinitionRow,
    PriceDefinitionValidation,
    ProductCalculationDetailsContent,
    productCalculationDetailsContentDecoder,
    ProductCalculationDetailsState
} from './ProductCalculationDetailsState'
import {Session} from '../../Data/Session'
import {decodeHttpResponse, HttpError} from '../../Data/Http'
import * as ProductCalculationResource from '../../Request/ProductCalculationResource'
import {messageWithTranslationDecoder} from '../../Entities/MessageWithTranslation.entity.evolve'
import {array, Decoder} from 'json-bouncer'
import {update} from '../../Data/ObjectHelper'
import {
    PriceDefinition,
    PriceDefinitionProductCalculationPatch
} from '../../Entities/PriceDefinition'
import {
    arrayHasUniqueStringKeyValidator,
    mandatoryStringValidator,
    stringToFixedPointNumberValidator,
    stringToIntValidator,
    validateInputField
} from '../../Data/FormValidation'
import {formatPrice} from '../../Data/NumberHelper'
import * as MessageResource from '../../Request/MessageResource'
import {Customer} from '../../Entities/Customer'
import {HttpRequest} from '../../Data/HttpRequest'
import {FeedbackLoop} from '../../Data/FeedbackLoop'

export function activate(id: number): HttpRequest {
    return ProductCalculationResource.retrieve(id)
}

export function initialDecoder(): Decoder<ProductCalculationDetailsContent> {
    return productCalculationDetailsContentDecoder()
}

export function init(
    content: ProductCalculationDetailsContent
): ProductCalculationDetailsState {
    let priceDefinitionList = R.map(
        (priceDefinition: PriceDefinition) => {
            return {
                origin: priceDefinition,
                input: {
                    quantity: priceDefinition.minQuantity.toString(),
                    price: formatPrice(priceDefinition.netVariableCosts, 3)
                },
                validation: {
                    quantity: null,
                    price: null
                },
                deleted: false
            }
        },
        content.productCalculationWithAssociations.priceDefinitions
    )

    return {
        content,
        priceDefinitionList: priceDefinitionList,
        editable: {type: 'Locked'},

        typeSearch: content.productCalculationWithAssociations.typeMessage.translation,
        typeSuggestions: [],
        selectedTypeId: content.productCalculationWithAssociations.typeMessage.id,

        formatSearch: content.productCalculationWithAssociations.formatMessage.translation,
        formatSuggestions: [],
        selectedFormatId: content.productCalculationWithAssociations.formatMessage.id,

        coverageSearch: content.productCalculationWithAssociations.coverageMessage.translation,
        coverageSuggestions: [],
        selectedCoverageId: content.productCalculationWithAssociations.coverageMessage.id,

        printSearch: content.productCalculationWithAssociations.printMessage.translation,
        printSuggestions: [],
        selectedPrintId: content.productCalculationWithAssociations.printMessage.id,

        paperSearch: content.productCalculationWithAssociations.paperMessage.translation,
        paperSuggestions: [],
        selectedPaperId: content.productCalculationWithAssociations.paperMessage.id,

        processingSearch: content.productCalculationWithAssociations.processingMessage.translation,
        processingSuggestions: [],
        selectedProcessingId: content.productCalculationWithAssociations.processingMessage.id,

        weightInput: content.productCalculationWithAssociations.productCalculation.weight.toString(),
        weightValidation: null,
        selectedShippingService:
        content.productCalculationWithAssociations.productCalculation.productShippingServiceId,
        submissionAttempted: false,

        selectedCustomers: R.map(
            (customer: Customer) => {
                return customer.id
            },
            content.productCalculationWithAssociations.customers
        ),
        selectedCustomersValidation: null,

        createNewMessageModal: null,
        createNewMessageModalInput: '',
        createNewMessageModalInputValidation: null,
        createNewMessageModalTranslation: '',
        createNewMessageModalTranslationValidation: null,
        newMessageSubmissionAttempted: false,
        error: null,
    }
}

type ProductCalculationDetailsLoop =
    FeedbackLoop<ProductCalculationDetailsState, ProductCalculationDetailsFeedback>

export function reduce(
    session: Session,
    state: ProductCalculationDetailsState,
    action: ProductCalculationDetailsAction
): ProductCalculationDetailsLoop {
    switch (action.type) {
        case 'MenuButtonClicked':
            return {
                state,
                feedback: [{type: 'OpenDrawer'}],
            }
        case 'ProductCalculationSaveResponse':
            const result =
                decodeHttpResponse(
                    productCalculationDetailsContentDecoder(),
                    action.result
                )
            return (() => {
                switch (result.type) {
                    case 'Ok':
                        return {
                            state: init(result.value),
                            feedback: [],
                        }
                    case 'Err':
                        return {
                            state: update({error: result.error}, state),
                            feedback: []
                        }
                }
            })()
        case 'CancelCreateNewMessageModal':
            return {
                state: update({
                        createNewMessageModal: null,
                        createNewMessageModalInput: '',
                        createNewMessageModalTranslation: '',
                    },
                    state),
                feedback: []
            }
        case 'ProductCalculationMessageSearchChanged':

            let searchState = {...state}

            searchState[action.key + 'Search'] = action.value
            return {
                state: searchState,
                feedback: []
            }
        case 'ClearProductCalculationMessageSuggestions':

            let clearState = {...state}
            clearState[action.key + 'Suggestions'] = []

            return {
                state: clearState,
                feedback: []
            }
        case 'FetchProductCalculationMessageSuggestions':
            return {
                state,
                feedback:
                    [{
                        type: 'SendRequest',
                        identifier: {
                            type: 'ProductCalculationMessageSuggestionList',
                            key: action.key
                        },
                        httpRequest:
                            ProductCalculationResource.listMessageSuggestions(
                                session.manufacturer.id,
                                action.key,
                                action.value
                            ),
                    }]
            }
        case 'ProductCalculationMessageSuggestionsResponse':
            const messages =
                decodeHttpResponse(
                    array(messageWithTranslationDecoder()),
                    action.result)

            let suggestionsState = {...state}

            suggestionsState[action.key + 'Suggestions'] = messages.type === 'Ok' ? messages.value : []

            return {
                state: suggestionsState,
                feedback: []
            }
        case 'SaveNewMessageResponse':
            const newCreatedMessage =
                decodeHttpResponse(
                    messageWithTranslationDecoder(),
                    action.result)
            let newState = {...state}
            if (newCreatedMessage.type !== 'Ok') {
                return {
                    // Processing a Fetch Errro
                    state,
                    feedback: []
                }
            }
            switch (action.key) {
                case 'type':
                    newState.selectedTypeId = newCreatedMessage.value.id
                    newState.typeSearch = newCreatedMessage.value.translation
                    break
                case 'format':
                    newState.selectedFormatId = newCreatedMessage.value.id
                    newState.formatSearch = newCreatedMessage.value.translation
                    break
                case 'coverage':
                    newState.selectedCoverageId = newCreatedMessage.value.id
                    newState.coverageSearch = newCreatedMessage.value.translation
                    break
                case 'print':
                    newState.selectedPrintId = newCreatedMessage.value.id
                    newState.printSearch = newCreatedMessage.value.translation
                    break
                case 'paper':
                    newState.selectedPaperId = newCreatedMessage.value.id
                    newState.paperSearch = newCreatedMessage.value.translation
                    break
                case 'processing':
                    newState.selectedProcessingId = newCreatedMessage.value.id
                    newState.processingSearch = newCreatedMessage.value.translation
                    break
            }
            newState.createNewMessageModal = null
            newState.createNewMessageModalInput = ''
            newState.createNewMessageModalTranslation = ''
            return {
                state: newState,
                feedback: []
            }
        case 'NewMessageInputChange':
            switch (action.language) {
                default:
                case 'EN':
                case 'origin':
                    return {
                        state: update({
                                createNewMessageModalInput: action.value,
                                createNewMessageModalInputValidation: validateInputField(
                                    mandatoryStringValidator(),
                                    state.newMessageSubmissionAttempted,
                                    action.value,
                                ),
                            },
                            state),
                        feedback: []
                    }
                case 'DE':
                case 'translation':
                    return {
                        state: update({
                                createNewMessageModalTranslation: action.value,
                                createNewMessageModalTranslationValidation: validateInputField(
                                    mandatoryStringValidator(),
                                    state.newMessageSubmissionAttempted,
                                    action.value,
                                ),
                            },
                            state),
                        feedback: []
                    }
            }
        case 'SaveNewMessage':
            if (
                state.createNewMessageModalInputValidation === null
                && state.createNewMessageModalTranslationValidation === null
                && state.createNewMessageModal !== null
            ) {
                return {
                    state: update({
                            newMessageSubmissionAttempted: true
                        },
                        state),
                    feedback: [{
                        type: 'SendRequest',
                        identifier: {
                            type: 'SaveNewMessageIdentifier',
                            key: state.createNewMessageModal
                        },
                        httpRequest:
                            MessageResource.createWithTranslation(
                                state.createNewMessageModalInput,
                                state.createNewMessageModalTranslation
                            ),
                    }],
                }
            } else {
                return {
                    state: update({
                            newMessageSubmissionAttempted: true
                        },
                        state),
                    feedback: [],
                }
            }
        case 'ProductCalculationMessageChanged':
            if (action.value.id === 0) {
                return {
                    state: update({
                            createNewMessageModal: action.key,
                            selectedTypeId: state.selectedTypeId
                        },
                        state),
                    feedback: []
                }
            }

            switch (action.key) {
                case 'type':
                    return {
                        state: update({selectedTypeId: action.value.id}, state),
                        feedback: []
                    }
                case 'format':
                    return {
                        state: update({selectedFormatId: action.value.id}, state),
                        feedback: []
                    }
                case 'coverage':
                    return {
                        state: update({selectedCoverageId: action.value.id}, state),
                        feedback: []
                    }
                case 'print':
                    return {
                        state: update({selectedPrintId: action.value.id}, state),
                        feedback: []
                    }
                case 'paper':
                    return {
                        state: update({selectedPaperId: action.value.id}, state),
                        feedback: []
                    }
                case 'processing':
                    return {
                        state: update({selectedProcessingId: action.value.id}, state),
                        feedback: []
                    }
            }
            return {
                state,
                feedback: []
            }
        case 'ProductCalculationShippingServiceChanged':
            return {
                state: update(
                    {
                        selectedShippingService: action.value
                    },
                    state),
                feedback: []
            }
        case 'ProductCalculationWeightChanged':

            return {
                state: update(
                    {
                        weightInput: action.value,
                        weightValidation: validateInputField(
                            weightValidator,
                            state.submissionAttempted,
                            action.value
                        )
                    },
                    state),
                feedback: []
            }
        case 'EditProductCalculation':
            return {
                state: update(
                    {
                        editable: {type: 'Pending', value: null}
                    },
                    state),
                feedback: []
            }
        case 'DiscardProductCalculation':
            return {
                state: init(state.content),
                feedback: []
            }
        case 'ChangeProductCalculationCustomer':
            return {
                state: update({
                    selectedCustomers: action.values
                },
                    state),
                feedback: []
            }
        case 'ToogleEditablePriceDefinitionRow':
            return {
                state: update(
                    {
                        priceDefinitionList: R.map(
                            (editablePriceDefinitionRow: EditablePriceDefinitionRow) => {
                                if (editablePriceDefinitionRow.origin.id === action.id) {
                                    if (editablePriceDefinitionRow.deleted) {
                                        return update(
                                            {
                                                deleted: false
                                            },
                                            editablePriceDefinitionRow)
                                    } else {
                                        return update(
                                            {
                                                deleted: true
                                            },
                                            editablePriceDefinitionRow)
                                    }
                                }
                                return editablePriceDefinitionRow
                            },
                            state.priceDefinitionList
                        )
                    },
                    state),
                feedback: []
            }
        case 'PriceDefinitionPositionRowQuantityChange':
            return {
                state: update(
                    {
                        priceDefinitionList: R.map(
                            (editablePriceDefinitionRow: EditablePriceDefinitionRow) => {
                                if (editablePriceDefinitionRow.origin.id === action.id) {
                                    return update(
                                        {
                                            input: update(
                                                {
                                                    quantity: action.value
                                                },
                                                editablePriceDefinitionRow.input),
                                            validation: update(
                                                {
                                                    quantity: validateInputField(
                                                        getQuantityValidator(state.priceDefinitionList),
                                                        state.submissionAttempted,
                                                        action.value
                                                    )
                                                },
                                                editablePriceDefinitionRow.validation
                                            )
                                        },
                                        editablePriceDefinitionRow)
                                }
                                return editablePriceDefinitionRow
                            },
                            state.priceDefinitionList
                        )
                    },
                    state),
                feedback: []
            }
        case 'PriceDefinitionPositionRowPriceChange':
            return {
                state: update(
                    {
                        priceDefinitionList: R.map(
                            (editablePriceDefinitionRow: EditablePriceDefinitionRow) => {
                                if (editablePriceDefinitionRow.origin.id === action.id) {
                                    return update({
                                            input: update(
                                                {
                                                    price: action.value
                                                },
                                                editablePriceDefinitionRow.input),
                                            validation: update(
                                                {
                                                    price: validateInputField(
                                                        positionRowValiators.price,
                                                        state.submissionAttempted,
                                                        action.value
                                                    )
                                                },
                                                editablePriceDefinitionRow.validation
                                            )
                                        },
                                        editablePriceDefinitionRow)
                                }
                                return editablePriceDefinitionRow
                            },
                            state.priceDefinitionList
                        )
                    },
                    state),
                feedback: []
            }
        case 'CreatePriceDefinitionPositionRow':
            return {
                state: update(
                    {
                        priceDefinitionList: R.append(
                            {
                                origin: {
                                    id: R.reduce((previous, priceDefinitionEditable: EditablePriceDefinitionRow) => {
                                            if (previous <= priceDefinitionEditable.origin.id) {
                                                return priceDefinitionEditable.origin.id + 1
                                            }
                                            return previous
                                        },
                                        0,
                                        state.priceDefinitionList
                                    ),
                                    priceGroupId: 0,
                                    minQuantity: 0,
                                    netFixedCosts: 0,
                                    grossFixedCosts: null,
                                    netVariableCosts: 0,
                                    grossVariableCosts: null,
                                },
                                input: {
                                    quantity: '1',
                                    price: '0.99'
                                },
                                validation: {
                                    quantity: null,
                                    price: null
                                },
                                deleted: false
                            },
                            state.priceDefinitionList)
                    },
                    state),
                feedback: []
            }
        case 'SaveProductCalculation':
            let rowValidation: ValidationOutcome = validatePositionRows(state.priceDefinitionList)
            let weightValidation = weightValidator(state.weightInput)
            if (rowValidation.data === null || weightValidation.type === 'Err') {
                return {
                    state: update(
                        {
                            priceDefinitionList: rowValidation.priceDefinitionRows,
                            weightValidation: (weightValidation.type === 'Err') ? weightValidation.error : null,
                            submissionAttempted: true
                        },
                        state),
                    feedback: []
                }
            }
            return {
                state: update(
                    {
                        editable: {type: 'Loading', value: null},
                        submissionAttempted: true
                    },
                    state),
                feedback: [{
                    type: 'SendRequest',
                    identifier: {type: 'SaveProductCalculation'},
                    httpRequest:
                        ProductCalculationResource.update(
                            state.content.productCalculationWithAssociations.productCalculation.id,
                            {
                                typeMessageId: state.selectedTypeId,
                                formatMessageId: state.selectedFormatId,
                                coverageMessageId: state.selectedCoverageId,
                                printMessageId: state.selectedPrintId,
                                paperMessageId: state.selectedPaperId,
                                processingMessageId: state.selectedProcessingId,
                                weight: weightValidation.value,
                                productShippingServiceId: state.selectedShippingService,
                                priceDefinitions: rowValidation.data,
                                customers: R.filter(
                                    (customer: Customer) => {
                                        return state.selectedCustomers.indexOf(customer.id) !== -1
                                    },
                                    state.content.availableCustomers
                                )
                            }
                        ),
                }],
            }

    }
}

export function httpResponseAction(
    identifier: ProductCalculationDetailsRequestIdentifier,
    result: Result.Result<HttpError, string>
): ProductCalculationDetailsAction {
    switch (identifier.type) {
        case 'ProductCalculationMessageSuggestionList':
            return {
                type: 'ProductCalculationMessageSuggestionsResponse',
                key: identifier.key,
                result,
            }
        case 'SaveProductCalculation':
            return {
                type: 'ProductCalculationSaveResponse',
                result,
            }
        case 'SaveNewMessageIdentifier':
            return {
                type: 'SaveNewMessageResponse',
                key: identifier.key,
                result,
            }
    }
}

interface ValidationOutcome {
    priceDefinitionRows: EditablePriceDefinitionRow[]
    data: PriceDefinitionProductCalculationPatch[] | null
}

function validatePositionRows(editablePriceDefinitionRows: EditablePriceDefinitionRow[]): ValidationOutcome {
    return R.reduce(
        (previous: ValidationOutcome,
         editablePriceDefinitionRow: EditablePriceDefinitionRow): ValidationOutcome => {
            let validatedRow = validatePositionRow(editablePriceDefinitionRow, editablePriceDefinitionRows)

            if (validatedRow.type === 'Err') {
                return {
                    priceDefinitionRows: R.append(
                        update(
                            {
                                validation: validatedRow.error
                            },
                            editablePriceDefinitionRow),
                        previous.priceDefinitionRows),
                    data: null
                }
            } else {
                if (previous.data === null) {
                    return {
                        priceDefinitionRows: R.append(editablePriceDefinitionRow, previous.priceDefinitionRows),
                        data: null
                    }
                }
                return {
                    priceDefinitionRows: R.append(editablePriceDefinitionRow, previous.priceDefinitionRows),
                    data: R.append(validatedRow.value, previous.data)
                }
            }
        },
        {
            priceDefinitionRows: [],
            data: []
        },
        editablePriceDefinitionRows
    )
}

function validatePositionRow(
    editablePriceDefinitionRow: EditablePriceDefinitionRow,
    editablePriceDefinitionRows: EditablePriceDefinitionRow[]
): Result.Result<PriceDefinitionValidation, PriceDefinitionProductCalculationPatch> {
    const
        {input} = editablePriceDefinitionRow,
        validationResult = {
            quantity: getQuantityValidator(editablePriceDefinitionRows)(input.quantity),
            price: positionRowValiators.price(input.price),
        }
    if (validationResult.quantity.type === 'Ok' &&
        validationResult.price.type === 'Ok'
    ) {
        return {
            type: 'Ok',
            value: {
                id: editablePriceDefinitionRow.origin.id,
                minQuantity: validationResult.quantity.value,
                netVariableCosts: validationResult.price.value,
                deleted: editablePriceDefinitionRow.deleted
            }
        }
    } else {
        return {
            type: 'Err',
            error: {
                quantity: Result.errorToNullable(validationResult.quantity),
                price: Result.errorToNullable(validationResult.price),
            }
        }
    }
}

function getQuantityValidator(arrayList: EditablePriceDefinitionRow[]) {
    return Result.compose(
        Result.compose(
            stringToIntValidator(),
            mandatoryStringValidator()
        ),
        arrayHasUniqueStringKeyValidator(arrayList, (elem: EditablePriceDefinitionRow) => elem.input.quantity)
    )
}

const positionRowValiators = {
    quantity: Result.compose(
        stringToIntValidator(),
        mandatoryStringValidator(),
    ),
    price: Result.compose(
        stringToFixedPointNumberValidator(3),
        mandatoryStringValidator(),
    ),
}

const weightValidator = Result.compose(
    stringToIntValidator(),
    mandatoryStringValidator(),
)



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Pages/ProductCalculationDetails/ProductCalculationDetailsReducer.ts